import React, { useMemo } from 'react';
import SimpleBar from 'simplebar-react';
import { useTranslation } from 'react-i18next';
import { AnomalyMetricKey, AnomalyMetricSemantics } from '../../../../../../../shared/models/anomaly-detection/models/AnomalyIncidentsResponse';
import {
  buildAnomalyPeriodLabel,
  formatAnomalyContributorValue,
  formatAnomalyDeltaValue,
  getAnomalyAccentColor,
  getAnomalyContributorIconClass,
  getAnomalyDirectionIconClass,
} from '../../../../../../../shared/helpers/AnomalyIncidentHelpers';
import { GroupedAnomalyMarker } from '../../groupedAnomalyMarkers';
import {
  buildAnomalyMetricLabels,
  collectMarkerMetricIncidents,
  compareAnomalyMetrics,
  getAnomalyMetricLabel,
  getAnomalyMetricVerb,
  MarkerMetricIncident,
} from './anomalyMetricConfig';
import {
  mergeAnomalyContributors,
  MergedContributor,
  MergedContributorImpact,
} from './mergeAnomalyContributors';

const ImpactRow: React.FC<{
  impact: MergedContributorImpact;
  metricLabel: string;
}> = ({ impact, metricLabel }) => {
  const iconClass = getAnomalyContributorIconClass(impact.type);
  const accent = getAnomalyAccentColor(impact.direction);
  const valueLabel = formatAnomalyContributorValue(
    impact.type,
    impact.value,
    impact.unit,
    impact.direction,
  );

  return (
    <div className="anomaly-tooltip__impact-row">
      <span className="anomaly-tooltip__impact-metric">{metricLabel}</span>
      <span className={`ico ico--xs ${iconClass}`} style={{ backgroundColor: accent }} aria-hidden="true" />
      <strong className="anomaly-tooltip__impact-value">{valueLabel}</strong>
    </div>
  );
};

const MergedContributorItem: React.FC<{
  contributor: MergedContributor;
  metricLabels: Record<AnomalyMetricKey, string>;
}> = ({ contributor, metricLabels }) => {
  const sortedImpacts = [...contributor.impacts].sort((left, right) =>
    compareAnomalyMetrics(left.metric, right.metric),
  );

  return (
    <li className="anomaly-tooltip__merged-item">
      <span className="anomaly-tooltip__merged-label">{contributor.label}</span>
      <div className="anomaly-tooltip__impact-list">
        {sortedImpacts.map((impact) => (
          <ImpactRow
            key={`${contributor.key}-${impact.metric}`}
            impact={impact}
            metricLabel={metricLabels[impact.metric]}
          />
        ))}
      </div>
    </li>
  );
};

const AnomalyMetricSummary: React.FC<{
  metricIncident: MarkerMetricIncident;
  dateKey: string;
  dateFormatter?: (raw: string) => string;
}> = ({ metricIncident, dateKey, dateFormatter }) => {
  const { t } = useTranslation();
  const { incident, metric, unit: valueUnit } = metricIncident;
  const accent = getAnomalyAccentColor(incident.direction);
  const directionIconClass = getAnomalyDirectionIconClass(incident.direction);
  const metricName = getAnomalyMetricLabel(t, metric);
  const verb = getAnomalyMetricVerb(t, metric, incident.direction);
  const periodLabel = buildAnomalyPeriodLabel(incident, dateKey, dateFormatter);

  const formatVal = (value: number): string => `${value}${valueUnit}`;
  const currentVal = incident.currentValue != null ? formatVal(incident.currentValue) : '–';
  const baselineVal = incident.baselineValue != null ? formatVal(incident.baselineValue) : '–';
  const delta =
    incident.currentValue != null && incident.baselineValue != null
      ? `${formatAnomalyDeltaValue(
          incident.currentValue - incident.baselineValue,
          incident.direction,
        )}${valueUnit}`
      : null;

  const headline = `${metricName} ${verb} ${baselineVal} ${t('anomalyHeadlineTo')} ${currentVal}`;

  return (
    <div className="anomaly-tooltip__metric-summary" style={{ borderLeftColor: accent }}>
      <div className="anomaly-tooltip__metric-summary-top">
        <span className="anomaly-tooltip__metric-name">{metricName}</span>
        <span className="anomaly-tooltip__direction">
          <span className="anomaly-tooltip__direction-icon">
            {directionIconClass ? (
              <span className={`ico ico--xs ${directionIconClass}`} aria-hidden="true" />
            ) : (
              '–'
            )}
          </span>
          <span className="anomaly-tooltip__direction-label" style={{ color: accent }}>
            {delta ?? '–'}
          </span>
        </span>
      </div>
      <span className="anomaly-tooltip__metric-date">{periodLabel}</span>
      <p className="anomaly-tooltip__metric-headline">{headline}</p>
    </div>
  );
};

interface AnomalyMultipleMetricsTooltipProps {
  marker: GroupedAnomalyMarker;
  metricSemantics: Record<AnomalyMetricKey, AnomalyMetricSemantics>;
  dateFormatter?: (raw: string) => string;
}

const AnomalyMultipleMetricsTooltip: React.FC<AnomalyMultipleMetricsTooltipProps> = ({
  marker,
  metricSemantics,
  dateFormatter,
}) => {
  const { t } = useTranslation();

  const metricIncidents = useMemo(() => collectMarkerMetricIncidents(marker), [marker]);

  const { prompts, models } = useMemo(
    () => mergeAnomalyContributors(metricIncidents, metricSemantics),
    [metricIncidents, metricSemantics],
  );

  if (metricIncidents.length < 2) {
    return null;
  }

  const metricLabels = buildAnomalyMetricLabels(t);

  return (
    <div className="anomaly-tooltip">
      <div className="anomaly-tooltip__body">
        <div className="anomaly-tooltip__metric-summaries">
          {metricIncidents.map((metricIncident) => (
            <AnomalyMetricSummary
              key={metricIncident.metric}
              metricIncident={metricIncident}
              dateKey={marker.dateKey}
              dateFormatter={dateFormatter}
            />
          ))}
        </div>

        {(prompts.length > 0 || models.length > 0) && (
          <SimpleBar className="anomaly-tooltip__drivers-scroll" autoHide={false}>
            <div className="anomaly-tooltip__drivers">
              {prompts.length > 0 && (
                <div className="anomaly-tooltip__section">
                  <div className="anomaly-tooltip__section-label">{t('anomalyTooltipTopPrompts')}</div>
                  <ul className="anomaly-tooltip__list anomaly-tooltip__list--merged">
                    {prompts.map((contributor) => (
                      <MergedContributorItem
                        key={contributor.key}
                        contributor={contributor}
                        metricLabels={metricLabels}
                      />
                    ))}
                  </ul>
                </div>
              )}
              {models.length > 0 && (
                <div className="anomaly-tooltip__section">
                  <div className="anomaly-tooltip__section-label">{t('anomalyTooltipTopModels')}</div>
                  <ul className="anomaly-tooltip__list anomaly-tooltip__list--merged">
                    {models.map((contributor) => (
                      <MergedContributorItem
                        key={contributor.key}
                        contributor={contributor}
                        metricLabels={metricLabels}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </SimpleBar>
        )}
      </div>
    </div>
  );
};

export default AnomalyMultipleMetricsTooltip;
